import { useQuery } from '@tanstack/react-query';

import { fetchStaff, type StaffMember } from '../../../../api/staff';

/** Auth0 role key for Regional Directors (mirrors backend ROLE_KEYS). */
export const REGIONAL_DIRECTOR_ROLE = 'regional-director';

/** Staff who hold the Regional Director role — the only people assignable as a
 *  region's director. Roles are sourced from Auth0, so this is empty until a
 *  user has been granted the RD role. */
export function useRegionalDirectors() {
  return useQuery<StaffMember[]>({
    queryKey: ['staff', 'regional-directors'],
    queryFn: async () =>
      (await fetchStaff()).filter((s) => s.roles?.includes(REGIONAL_DIRECTOR_ROLE)),
    staleTime: 60_000,
  });
}

export interface DirectorOption {
  id: string;
  name: string;
}

/**
 * The selectable director options: every RD, plus the currently-assigned
 * director if (legacy data) they no longer hold the RD role — so an existing
 * assignment never silently disappears from the dropdown.
 */
export function directorOptions(
  rds: StaffMember[],
  currentId: string | null,
  currentName?: string | null,
): DirectorOption[] {
  const options = rds.map((s) => ({ id: s.id, name: s.name }));
  if (currentId && !options.some((o) => o.id === currentId)) {
    options.unshift({ id: currentId, name: currentName || 'Currently assigned' });
  }
  return options;
}
